import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums';
import { insurerUzoviAddSchema, insurerUzoviEditSchema } from '@common/validation/insurerUzovi';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    OverlapChecker,
    RateLimiter,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import type { IBreadcrumbItem } from '../../elements/common';
import type { IInsurer } from './InsurerUzovisList';
import {
    CancelButton,
    DangerAlertWhenErrorCode,
    FormContainer,
    Header,
    Loading,
    OverlapMessage,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { DateField } from '../../fields/Date';
import { DropdownAsyncLoadField } from '../../fields/DropdownAsyncLoad';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

type InsurerUzoviAddEditProps = IWithRouterProps;

interface IInsurerUzovi {
    endDate: string | null;
    id?: string;
    insurerId?: string;
    startDate: string;
    uzoviId: string;
}

interface IUzovi {
    code: string;
    id: string;
    name: string;
}

interface IInsurerUzoviAddEditState {
    errorCode?: string;
    errors: ValidationErrors;
    insurer: IInsurer;
    insurerUzovi?: IInsurerUzovi;
    isSaving: boolean;
    overlap?: {
        endDate: string | null;
        id: string;
        insurer: {
            id: string;
            name: string;
        };
        startDate: string;
    };
    uzovis: IUzovi[];
}

class InnerInsurerUzoviAddEdit extends Component<InsurerUzoviAddEditProps, IInsurerUzoviAddEditState> {
    private readonly atMostOncePerSecond = new RateLimiter(1000);
    private readonly overlapChecker = new OverlapChecker('/api/insurerUzovis');

    constructor(props: InsurerUzoviAddEditProps) {
        super(props);

        assertHasAuthorization(ManagerAuthorization.insurersAddEdit);

        this.state = {
            insurer: {
                id: '',
                name: '',
            },
            uzovis: [],
            isSaving: false,
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        if (this.props.router.params.id) {
            this.load();
        } else {
            await this.setState({
                insurerUzovi: {
                    uzoviId: '',
                    startDate: '',
                    endDate: null,
                },
            });
        }

        this.loadInsurer();
    }

    public async componentDidUpdate(prevProps: InsurerUzoviAddEditProps) {
        if (this.props.router.params.id !== prevProps.router.params.id) {
            await this.loadInsurer();
            await this.load();
            await this.checkOverlap();
        }
    }

    public render() {
        const insurerId = this.props.router.searchParams.get('insurerId');
        const breadcrumbs: IBreadcrumbItem[] = [{ text: translateKey('insurers.insurerUzovis') }];
        if (this.props.router.params.id) {
            breadcrumbs.push({ text: translateKey('common.edit') });
        } else {
            breadcrumbs.push({ text: translateKey('common.new') });
        }
        breadcrumbs.push({ text: this.state.insurer.name, url: `/insurers/${this.state.insurer.id}` });

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header
                    content={
                        translateKey('insurers.insurerUzovis') +
                        ' ' +
                        (this.props.router.params.id ? translateKey('common.edit') : translateKey('common.new'))
                    }
                />
                <DangerAlertWhenErrorCode errorCode={this.state.errorCode} />
                <Loading
                    item={this.state.insurerUzovi}
                    loaded={(insurerUzovi) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            {this.state.overlap && (
                                <OverlapMessage
                                    endDate={this.state.overlap.endDate}
                                    link={`/insurerUzovis/${this.state.overlap.id}?insurerId=${this.state.overlap.insurer.id}`}
                                    message={translateKey(
                                        'insurers.insurerUzoviOverlapMessage',
                                        this.state.overlap.insurer.name
                                    )}
                                    startDate={this.state.overlap.startDate}
                                />
                            )}

                            <FormContextProvider
                                value={{
                                    placeholderGroupName: '',
                                    item: insurerUzovi,
                                    errors: this.state.errors,
                                    onChange: async (key, value) => await this.handleChange(key, value),
                                    onDoValidate: async (key) => await this.validateOne(key),
                                }}
                            >
                                <DropdownAsyncLoadField
                                    clearable={false}
                                    disabled={!!this.props.router.params.id}
                                    fieldKey="uzoviId"
                                    isRequired={true}
                                    label={translateKey('uzovi.uzovi')}
                                    loadOptions={async (searchText) => await this.loadUzovis(searchText)}
                                    onChange={async (value: string | null) => {
                                        if (value) {
                                            await this.handleChange('uzoviId', value);
                                        }
                                    }}
                                    placeholder={translateKey('uzovi.uzovi')}
                                    value={insurerUzovi.uzoviId}
                                />
                                <DateField
                                    fieldKey="startDate"
                                    isRequired={true}
                                    label={translateKey('common.startDate')}
                                    placeholder={translateKey('common.startDate')}
                                    size="medium"
                                />
                                <DateField
                                    fieldKey="endDate"
                                    isRequired={false}
                                    label={translateKey('common.endDate')}
                                    placeholder={translateKey('common.endDate')}
                                    size="medium"
                                />
                            </FormContextProvider>

                            <CancelButton
                                disabled={this.state.isSaving}
                                to={'/insurers/' + insurerId}
                            />
                            <SaveSubmit disabled={this.state.isSaving || !!this.state.overlap} />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async handleChange<K extends keyof IInsurerUzovi>(name: K, value: IInsurerUzovi[K]) {
        await this.setState(({ insurerUzovi }) => ({ insurerUzovi: { ...insurerUzovi, [name]: value } }));
        await this.checkOverlap();
    }

    private async validateOne(name: keyof IInsurerUzovi) {
        await this.setState(({ insurerUzovi, errors }) => ({
            errors: updateValidationErrorsForOne(insurerUzoviEditSchema, insurerUzovi, errors, name),
        }));
    }

    private async load() {
        const url = '/api/insurerUzovis/' + this.props.router.params.id;
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurerUzovi: result.json });
    }

    private async loadInsurer() {
        const result = await fetchJson(`/api/insurers/${this.props.router.searchParams.get('insurerId')}`, 'GET');
        await this.setState({ insurer: result.json });
    }

    private async loadUzovis(searchText: string) {
        let url = '/api/uzovis';
        let urlQueryParams = '';
        if (searchText) {
            urlQueryParams += '&searchText=' + searchText;
        }
        if (this.state.insurerUzovi && this.state.insurerUzovi.uzoviId) {
            urlQueryParams += '&include=' + this.state.insurerUzovi.uzoviId;
        }
        if (urlQueryParams) {
            url += '?' + urlQueryParams;
        }
        const result = await fetchJson(url, 'GET');
        const options = result.json.items.map((item: IUzovi) => {
            return { value: item.id, label: item.code + ' - ' + item.name };
        });

        return options;
    }

    private async save() {
        let url = '/api/insurerUzovis/';
        const insurerId = this.props.router.searchParams.get('insurerId');
        const returnPage = this.props.router.searchParams.get('returnPage');

        if (this.props.router.params.id) {
            url += this.props.router.params.id;
        } else {
            await this.setState(({ insurerUzovi }) => {
                return { insurerUzovi: { ...insurerUzovi, insurerId } };
            });
        }

        const { errors, item } = validate(
            this.props.router.params.id ? insurerUzoviEditSchema : insurerUzoviAddSchema,
            this.state.insurerUzovi
        );
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        await this.setState({ isSaving: true });

        const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.OVERLAPPING_CONFIG]);
        if (result.errorCode) {
            await this.setState({ isSaving: false, errorCode: result.errorCode });
        } else {
            let returnUrl = '/insurers/' + insurerId;
            if (returnPage) {
                returnUrl += `?page=${returnPage}`;
            }
            this.props.router.navigate(returnUrl);
        }
    }

    private async checkOverlap() {
        this.atMostOncePerSecond.callMeMaybe(async () => {
            if (this.state.insurerUzovi && this.state.insurerUzovi.startDate) {
                const overlap = await this.overlapChecker.check(
                    {
                        uzoviId: this.state.insurerUzovi.uzoviId,
                    },
                    this.state.insurerUzovi.startDate,
                    this.state.insurerUzovi.endDate,
                    this.props.router.params.id
                );
                await this.setState({ overlap });
            }
        });
    }
}

export const InsurerUzoviAddEdit = withRouter(InnerInsurerUzoviAddEdit);
